import {compareAsc} from 'date-fns';

import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {eventsOverlap} from './eventsOverlap';

/**
 * Represents a group of events that can be displayed in parallel rows
 */
export interface EventRow {
  events: RentalResourceCalendarEvent[];
  rowIndex: number;
}

/**
 * Represents the collision layout for a group of events
 */
export interface CollisionLayout {
  rows: EventRow[];
  maxRowsNeeded: number;
}

/**
 * Detects collisions between events and organizes them into parallel rows
 * Events that overlap in time will be placed in separate rows
 *
 * @param events - Array of events to check for collisions
 * @returns Layout information showing how events should be arranged in rows
 *
 * @example
 * const events = [
 *   { id: '1', start: new Date('2024-01-01 09:00'), end: new Date('2024-01-01 11:00') },
 *   { id: '2', start: new Date('2024-01-01 10:00'), end: new Date('2024-01-01 12:00') },
 *   { id: '3', start: new Date('2024-01-01 13:00'), end: new Date('2024-01-01 15:00') }
 * ];
 *
 * const layout = detectEventCollisions(events);
 * // Returns layout with events organized in rows to avoid overlaps
 */
export function detectEventCollisions(events: RentalResourceCalendarEvent[]): CollisionLayout {
  if (events.length === 0) {
    return {
      rows: [],
      maxRowsNeeded: 0,
    };
  }

  if (events.length === 1) {
    return {
      rows: [
        {
          events: [events[0]],
          rowIndex: 0,
        },
      ],
      maxRowsNeeded: 1,
    };
  }

  // Sort events by start time using date-fns compareAsc
  const sortedEvents = [...events].sort((a, b) => compareAsc(a.start, b.start));

  const rows: EventRow[] = [];

  // Process each event
  sortedEvents.forEach((event) => {
    // Find the first row where this event doesn't collide with any existing event
    let placedInRow = false;

    for (const row of rows) {
      const hasCollision = row.events.some((existingEvent) => eventsOverlap(event, existingEvent));

      if (!hasCollision) {
        // Place event in this row
        row.events.push(event);
        placedInRow = true;
        break;
      }
    }

    // If we couldn't place the event in any existing row, create a new row
    if (!placedInRow) {
      rows.push({
        events: [event],
        rowIndex: rows.length,
      });
    }
  });

  return {
    rows,
    maxRowsNeeded: rows.length,
  };
}
